/**
 * Styled components for SmartChoiceDemo
 */
import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { PageBody } from '@abyss/web/ui/PageBody';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import { ToggleSwitch } from '@abyss/web/ui/ToggleSwitch';

// Main layout containers
export const Container = styled(Flex, {
  minHeight: '100vh',
  backgroundColor: '$gray2',
});

export const ControlPanel = styled('div', {
  width: '450px',
  padding: '24px',
  backgroundColor: '$white',
  borderRight: '1px solid $gray4',
  overflowY: 'auto',
  maxHeight: '100vh',
});

export const WidgetContainer = styled('div', {
  flex: 1,
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const WidgetWrapper = styled('div', {
  backgroundColor: '$white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  padding: '24px',
  width: '100%',
  maxWidth: '800px',
});

// Section components
export const Section = styled('div', {
  marginBottom: '28px',
});

export const SectionTitle = styled(Text, {
  fontWeight: '$bold',
  marginBottom: '20px',
  color: '$primary1',
  fontSize: '16px',
  display: 'block',
});

// Typography
export const StyledHeading = styled(Heading, {
  marginBottom: '16px',
});

export const StyledText = styled(Text, {
  marginBottom: '24px',
  color: '$gray6',
});

export const SmallText = styled(Text, {
  fontSize: '12px',
  color: '$gray6',
  marginTop: '8px',
  display: 'block',
});

// Code display
export const PropsDisplay = styled('pre', {
  backgroundColor: '$gray3',
  padding: '16px',
  borderRadius: '6px',
  fontSize: '12px',
  overflow: 'auto',
  maxHeight: '200px',
  margin: 0,
});

// Form elements
export const FormField = styled('div', {
  marginBottom: '16px',
  padding: '16px',
  backgroundColor: '$gray1',
  borderRadius: '8px',
  border: '1px solid $gray3',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '$gray5',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
});

export const FormFieldLabel = styled(Text, {
  display: 'block',
  fontWeight: '$semibold',
  marginBottom: '12px',
  color: '$gray8',
  fontSize: '14px',
});

// Styled ToggleSwitch with proper spacing
export const StyledToggleSwitch = styled(ToggleSwitch, {
  marginBottom: '4px',
  '& .abyss-toggle-switch-root': {
    gap: '12px',
  },
  '& .abyss-toggle-switch-label': {
    fontWeight: '$medium',
    marginLeft: '12px',
  },
  '& .abyss-toggle-switch-helper': {
    marginLeft: '12px',
    marginTop: '4px',
  },
  // Ensure proper alignment
  '& .abyss-toggle-switch-container': {
    alignItems: 'flex-start',
  },
});

// Radio group wrapper with proper spacing
export const RadioGroupWrapper = styled('div', {
  padding: '16px',
  backgroundColor: '$gray1',
  borderRadius: '8px',
  border: '1px solid $gray3',
  marginBottom: '16px',
  // Label styling
  '& .abyss-radio-group-label': {
    fontWeight: '$semibold',
    marginBottom: '16px',
    display: 'block',
    fontSize: '14px',
  },
  // Container for radio buttons
  '& .abyss-radio-group-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Individual radio button styling
  '& .abyss-radio-root': {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // Radio button circle
  '& .abyss-radio': {
    marginRight: '8px',
  },
  // Radio label text
  '& .abyss-radio-label': {
    marginLeft: '8px',
    fontSize: '14px',
    fontWeight: '$medium',
  },
  // Selected state
  '& input:checked ~ .abyss-radio-label': {
    fontWeight: '$bold',
  },
});

// Styled RadioGroup with proper spacing
export const StyledRadioGroup = styled(RadioGroup, {
  '.abyss-radio-group-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingTop: '8px',
  },
  '.abyss-radio-group-label': {
    fontWeight: '$semibold',
    fontSize: '14px',
    marginBottom: '4px',
  },
});

// Styled Radio button with proper spacing
export const StyledRadio = styled(RadioGroup.Radio, {
  '.abyss-radio': {
    borderColor: '$info1',
    marginRight: '12px',
  },
  '.abyss-radio-label': {
    fontWeight: '$medium',
    fontSize: '14px',
  },
  'input:checked ~ .abyss-radio': {
    borderColor: '$interactive1',
  },
  'input:checked ~ label': {
    fontWeight: '$bold',
  },
});

// Divider with proper spacing
export const StyledDivider = styled(Divider, {
  margin: '28px 0',
});

// Button groups
export const ButtonGroup = styled(Flex, {
  gap: '12px',
  flexWrap: 'wrap',
});

export const PresetButton = styled(Button, {
  fontSize: '12px',
});

export const PresetButtonGroup = styled(Flex, {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '20px',
});

// Mock state section styling
export const MockStateSection = styled(PageBody, {
  backgroundColor: '$core.color.brand.5',
  border: '1px solid $core.color.brand.20',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '24px',
});

export const MockStateTitle = styled(Text, {
  fontWeight: '$bold',
  color: '#92400e',
  fontSize: '15px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const MockStateDescription = styled('div', {
  fontSize: '13px',
  color: '$gray7',
  marginBottom: '16px',
  lineHeight: '1.5',
});

// Subsection within MockStateSection
export const MockStateSubsection = styled('div', {
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  border: '1px solid rgba(146, 64, 14, 0.2)',
  borderRadius: '6px',
  padding: '16px',
  marginBottom: '16px',
});

export const MockStateSubsectionTitle = styled(Text, {
  fontWeight: '$semibold',
  color: '#78350f',
  fontSize: '13px',
  marginBottom: '12px',
  display: 'block',
});

// Persistence indicator
export const PersistenceIndicator = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 14px',
  backgroundColor: '#ecfdf5',
  border: '1px solid #6ee7b7',
  borderRadius: '6px',
  marginBottom: '20px',
  fontSize: '13px',
  color: '#065f46',
});

// Mock state summary in preview area
export const MockStateSummary = styled('div', {
  backgroundColor: '#fffbeb',
  border: '1px solid #fbbf24',
  borderRadius: '8px',
  padding: '14px 18px',
  marginBottom: '20px',
  maxWidth: '800px',
  width: '100%',
});

export const MockStateSummaryTitle = styled(Text, {
  fontWeight: '$semibold',
  fontSize: '14px',
  color: '#92400e',
  marginBottom: '10px',
  display: 'block',
});

export const MockStateBadge = styled('span', {
  padding: '4px 10px',
  borderRadius: '4px',
  fontSize: '11px',
  fontFamily: 'monospace',
  variants: {
    active: {
      true: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
      },
      false: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
      },
    },
  },
});

export const MockStateBadgeContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

// Toggle group container for mock preferences
export const ToggleGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '12px',
});

// Event Log Section (simulates Redux DevTools)
export const EventLogSection = styled('div', {
  marginTop: '24px',
  padding: '16px',
  backgroundColor: '#1e1e1e',
  borderRadius: '8px',
  border: '1px solid #333',
});

export const EventLogHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
});

export const EventLogTitle = styled('h3', {
  fontSize: '14px',
  fontWeight: 600,
  color: '#10b981',
  margin: 0,
  fontFamily: 'monospace',
});

export const EventLogList = styled('div', {
  maxHeight: '300px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const EventLogItem = styled('div', {
  padding: '10px 12px',
  backgroundColor: '#2d2d2d',
  borderRadius: '4px',
  borderLeft: '3px solid',
  fontFamily: 'monospace',
  fontSize: '12px',
  variants: {
    eventType: {
      PREFERENCES_LOADED: {
        borderLeftColor: '#3b82f6',
      },
      PREFERENCES_SAVED: {
        borderLeftColor: '#10b981',
      },
      PREFERENCES_ERROR: {
        borderLeftColor: '#ef4444',
      },
    },
  },
});

export const EventLogAction = styled('div', {
  color: '#fbbf24',
  fontWeight: 600,
  marginBottom: '4px',
});

export const EventLogTimestamp = styled('span', {
  color: '#6b7280',
  fontSize: '10px',
  marginLeft: '8px',
});

export const EventLogPayload = styled('pre', {
  color: '#9ca3af',
  fontSize: '11px',
  margin: '4px 0 0 0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: '100px',
  overflow: 'auto',
});

export const EventLogEmpty = styled('div', {
  color: '#6b7280',
  fontSize: '12px',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '20px',
});

export const ClearLogButton = styled('button', {
  padding: '4px 8px',
  fontSize: '11px',
  backgroundColor: '#374151',
  color: '#9ca3af',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: 'monospace',
  '&:hover': {
    backgroundColor: '#4b5563',
    color: '#fff',
  },
});
